import { GraphQL } from "@rpi/openapi-api";
import axios from "axios";
import { retry } from "../utils/api.util";
import { filterNullableArray, isAxiosError } from "../utils/type.util";
import { ServiceObserver } from "./ServiceObserver";

interface UploadedPart {
  PartNumber: number;
  ETag: string;
}

interface UploadedFile {
  location: string;
  file_id: string;
  file_size: number;
  collection_id: string;
  upload_status: string;
  filename: string;
  file_md5: string | null;
}

/** The fields we read from axios's untyped (`any`) upload progress event. */
interface UploadProgressEvent {
  loaded: number;
}

function clampFraction(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

export class UploadService {
  static CHUNK_SIZE = 5 * 1024 * 1024; // 5mb

  readonly uploadCollection = ServiceObserver.bind(this._uploadCollection.bind(this));
  public async _uploadCollection(
    observer: ServiceObserver<{
      /** Fraction of files finished (0..1). */
      progress: number;
      /** Fraction of bytes transferred to storage (0..1); finer-grained than `progress`. */
      bytes: number;
      status: string;
    }>,
    collection: GraphQL.UploadServiceV2CollectionsStatusResponse,
    files: File[]
  ) {
    const uploadFiles = filterNullableArray(collection.uploadFiles || []);
    if (uploadFiles.length !== files.length) throw new Error(`${UploadService.name}: Not enough files.`);

    observer.emit("progress", 0);
    observer.emit("bytes", 0);

    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    const loadedByFile = files.map(() => 0);
    const emitBytesTransferred = () => {
      if (totalBytes <= 0) return;
      const loaded = loadedByFile.reduce((sum, value) => sum + value, 0);
      observer.emit("bytes", clampFraction(loaded / totalBytes));
    };

    let uploadedFilesCount = 0;
    const results = await Promise.all(
      uploadFiles.map(async (uploadData, i) => {
        const response = await this.uploadFile(collection, uploadData, files[i], (loaded) => {
          loadedByFile[i] = loaded;
          emitBytesTransferred();
        });
        observer.emit("progress", ++uploadedFilesCount / uploadFiles.length);
        return response.data;
      })
    );

    observer.emit("bytes", 1);
    return results;
  }

  private async uploadFile(
    collection: GraphQL.UploadServiceV2CollectionsStatusResponse,
    uploadData: GraphQL.UploadServiceV2FileStatusResponse,
    file: File,
    onProgress?: (loadedBytes: number) => void
  ) {
    if (!uploadData.multipartUpload?.parts) throw new Error(`${UploadService.name}: Missing upload information.`);
    const parts = filterNullableArray(uploadData.multipartUpload.parts);

    const createChunk = (partData: NonNullable<GraphQL.UploadServiceV2UploadPart>): Blob => {
      const start = (partData.PartNumber! - 1) * UploadService.CHUNK_SIZE;
      const end = Math.min(start + UploadService.CHUNK_SIZE, file.size);

      return file.slice(start, end);
    };

    const loadedByPart = new Map<number, number>();
    const reportPartProgress = (partNumber: number, loaded: number) => {
      loadedByPart.set(partNumber, loaded);
      const fileLoaded = [...loadedByPart.values()].reduce((sum, value) => sum + value, 0);
      onProgress?.(Math.min(fileLoaded, file.size));
    };

    const uploadedParts: UploadedPart[] = await Promise.all(
      parts
        .sort((a, b) => a.PartNumber! - b.PartNumber!)
        .map(async (partData) => {
          const chunk = createChunk(partData!);
          const response = await this.uploadPart(chunk, partData, (loaded) =>
            reportPartProgress(partData.PartNumber!, loaded)
          );

          if (!response?.headers.etag) {
            throw new Error(`${UploadService.name}: Missing ETag for part ${partData.PartNumber}.`);
          }

          return {
            PartNumber: partData.PartNumber!,
            ETag: response.headers.etag,
          };
        })
    );

    return (await this.completeFile(collection, uploadData, uploadedParts))!;
  }

  private async uploadPart(
    chunk: Blob,
    partData: GraphQL.UploadServiceV2UploadPart,
    onProgress?: (loadedBytes: number) => void
  ) {
    const apiUploadPart = async () => {
      return await axios.put(partData.uploadUrl!, chunk, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: onProgress ? (event: UploadProgressEvent) => onProgress(event.loaded) : undefined,
      });
    };

    return await retry(apiUploadPart, {
      retry: (retryCount, error) => {
        if (retryCount <= 5) return true;
        if (isAxiosError(error) && typeof error.status === "number" && error.status > 407) return true;
        return false;
      },
    })();
  }

  private async completeFile(
    collection: GraphQL.UploadServiceV2CollectionsStatusResponse,
    uploadData: GraphQL.UploadServiceV2FileStatusResponse,
    uploadedParts: UploadedPart[]
  ) {
    const apiCompleteFile = async () => {
      return await axios.post<UploadedFile>(collection.finishFileUrl, {
        upload_id: uploadData.multipartUpload?.uploadId,
        file_id: uploadData?.fileId,
        parts: uploadedParts,
      });
    };

    return await retry(apiCompleteFile, {
      retry: (retryCount, error) => {
        if (retryCount <= 5) return true;
        if (isAxiosError(error) && typeof error.status === "number" && error.status > 407) return true;
        return false;
      },
    })();
  }
}

export default new UploadService();
